import { create } from 'zustand';
import { applyNodeChanges, applyEdgeChanges, addEdge } from '@xyflow/react';
import { initialNodes, initialEdges } from '../components/flow/flowUtils';
import { runElkAutoLayout } from '../components/flow/elkLayout';
import api from '../services/api';

export const useWorkflowStore = create((set, get) => ({
  nodes: initialNodes,
  edges: initialEdges,
  selectedNodeId: null,
  draftHash: null,
  lastSavedAt: null,
  isPublished: false,
  versions: [],
  loading: false,

  setNodes: (nodes) => set({ nodes }),
  setEdges: (edges) => set({ edges }),
  setSelectedNodeId: (id) => set({ selectedNodeId: id }),

  onNodesChange: (changes) => {
    set({
      nodes: applyNodeChanges(changes, get().nodes),
    });
  },

  onEdgesChange: (changes) => {
    set({
      edges: applyEdgeChanges(changes, get().edges),
    });
  },

  onConnect: (connection) => {
    const newEdge = {
      ...connection,
      id: `e-${connection.source}-${connection.target}-${Date.now()}`,
      type: 'custom',
      animated: true,
    };
    set({
      edges: addEdge(newEdge, get().edges),
    });
  },

  addNode: (nodeData, position = { x: 350, y: 250 }) => {
    const newNode = {
      id: `node-${nodeData.type}-${Date.now()}`,
      type: nodeData.type,
      position,
      data: nodeData.data || { label: nodeData.title || nodeData.type, ...nodeData },
    };
    set({
      nodes: [...get().nodes, newNode],
      selectedNodeId: newNode.id,
    });
  },

  updateNodeData: (nodeId, dataUpdate) => {
    set({
      nodes: get().nodes.map((node) => {
        if (node.id === nodeId) {
          return {
            ...node,
            data: {
              ...node.data,
              ...dataUpdate,
            },
          };
        }
        return node;
      }),
    });
  },

  deleteNode: (nodeId) => {
    const node = get().nodes.find((n) => n.id === nodeId);
    if (node?.type === 'start') {
      return; // Do not delete start node
    }
    set({
      nodes: get().nodes.filter((n) => n.id !== nodeId),
      edges: get().edges.filter(
        (edge) => edge.source !== nodeId && edge.target !== nodeId
      ),
      selectedNodeId: get().selectedNodeId === nodeId ? null : get().selectedNodeId,
    });
  },

  duplicateNode: (nodeId) => {
    const sourceNode = get().nodes.find((n) => n.id === nodeId);
    if (!sourceNode || sourceNode.type === 'start') return;

    const newNode = {
      ...sourceNode,
      id: `node-${sourceNode.type}-${Date.now()}`,
      position: {
        x: sourceNode.position.x + 40,
        y: sourceNode.position.y + 40,
      },
      data: { ...sourceNode.data },
    };

    set({
      nodes: [...get().nodes, newNode],
      selectedNodeId: newNode.id,
    });
  },

  resetFlow: () => {
    set({
      nodes: initialNodes,
      edges: initialEdges,
      selectedNodeId: null,
    });
  },

  autoLayout: async () => {
    const nodes = get().nodes;
    const edges = get().edges;

    if (nodes.length === 0) return;

    const layoutedNodes = await runElkAutoLayout(nodes, edges);
    set({ nodes: layoutedNodes });
  },

  // ── Backend API Actions ─────────────────────────────────────────

  loadDraft: async (campaignId) => {
    set({ loading: true });
    try {
      const res = await api.get(`/campaigns/${campaignId}/flow/draft`);
      const { graph, hash, updated_at } = res.data;
      if (graph && Array.isArray(graph.nodes) && graph.nodes.length > 0) {
        set({
          nodes: graph.nodes,
          edges: graph.edges || [],
          draftHash: hash,
          lastSavedAt: updated_at,
          loading: false,
        });
      } else {
        // Fallback default template
        set({
          nodes: initialNodes,
          edges: initialEdges,
          draftHash: hash,
          lastSavedAt: updated_at,
          loading: false,
        });
      }
    } catch (err) {
      console.warn('Failed to fetch draft flow, loading initial template', err);
      set({
        nodes: initialNodes,
        edges: initialEdges,
        loading: false,
      });
    }
  },

  saveDraft: async (campaignId) => {
    const { nodes, edges, draftHash } = get();

    // Fetch active buyer sets to validate and sanitize route node buyer_set_ids
    let activeBuyerSets = [];
    try {
      const bsRes = await api.get(`/campaigns/${campaignId}/flow/buyer-sets`);
      activeBuyerSets = bsRes.data?.buyer_sets || [];
    } catch {
      try {
        const bsRes = await api.get('/buyer-sets');
        activeBuyerSets = Array.isArray(bsRes.data) ? bsRes.data : (bsRes.data?.buyer_sets || []);
      } catch {}
    }

    const validBuyerSetIds = activeBuyerSets.map((b) => b.id);
    const fallbackBuyerSetId = validBuyerSetIds[0];

    // Clean nodes/edges payload for backend graph format
    const cleanNodes = nodes.map((n) => {
      const nodeData = { ...(n.data || {}) };

      if (n.type === 'route') {
        const currentBsId = Number(nodeData.buyer_set_id);
        if (fallbackBuyerSetId && (!currentBsId || !validBuyerSetIds.includes(currentBsId))) {
          nodeData.buyer_set_id = fallbackBuyerSetId;
        }
      }

      return {
        id: n.id,
        type: n.type,
        position: n.position,
        data: nodeData,
      };
    });

    const cleanEdges = edges.map((e) => ({
      id: e.id,
      source: e.source,
      target: e.target,
      sourceHandle: e.sourceHandle || null,
      type: e.type || 'custom',
      label: e.label || undefined,
    }));

    // Update in-memory state if node data was sanitized
    set({
      nodes: nodes.map((n, idx) => ({ ...n, data: cleanNodes[idx].data })),
    });

    const payload = {
      graph: {
        nodes: cleanNodes,
        edges: cleanEdges,
      },
      hash: draftHash,
    };

    const res = await api.put(`/campaigns/${campaignId}/flow/draft`, payload);
    const { hash, updated_at } = res.data;
    set({
      draftHash: hash,
      lastSavedAt: updated_at,
    });
    return res.data;
  },

  publishFlow: async (campaignId) => {
    // First save draft
    await get().saveDraft(campaignId);
    const res = await api.post(`/campaigns/${campaignId}/flow/publish`);
    set({ isPublished: true });
    return res.data;
  },

  loadVersions: async (campaignId) => {
    try {
      const res = await api.get(`/campaigns/${campaignId}/flow/versions`);
      set({ versions: res.data || [] });
    } catch (err) {
      set({ versions: [] });
    }
  },

  rollbackVersion: async (campaignId, versionId) => {
    const res = await api.post(`/campaigns/${campaignId}/flow/versions/${versionId}/rollback`);
    await get().loadDraft(campaignId);
    return res.data;
  },

  applyTemplate: async (campaignId, templateId, overrideVerticalMismatch = false) => {
    const { draftHash } = get();
    const res = await api.post(`/campaigns/${campaignId}/flow/apply-template/${templateId}`, {
      hash: draftHash,
      override_vertical_mismatch: overrideVerticalMismatch,
    });
    // Response shape for a freshly-applied template isn't guaranteed to match
    // the draft GET/PUT payload, so re-fetch the draft rather than trusting
    // it - same reason rollbackVersion re-loads instead of trusting its own response.
    await get().loadDraft(campaignId);
    return res.data;
  },

  // Applies to whatever the backend currently has saved as this campaign's
  // draft, not the in-memory graph - save first so "Save as Template" never
  // silently snapshots stale, already-superseded edits.
  saveAsTemplate: async (campaignId, payload) => {
    await get().saveDraft(campaignId);
    const res = await api.post(`/campaigns/${campaignId}/flow/save-as-template`, payload);
    return res.data;
  },
}));
